import { useLayoutEffect, useState } from 'react';
export function useComposerPopupPlacement(active, popup, maxWidth = 420, maxHeight = 320) {
    const [placement, setPlacement] = useState();
    useLayoutEffect(() => {
        if (!active)
            return;
        const composer = popup.current?.closest('.agent-composer');
        const originElement = popup.current?.offsetParent;
        if (!composer || !(originElement instanceof HTMLElement))
            return;
        const boundary = composer.closest('[data-agent-composer-boundary]');
        const update = () => {
            const anchor = composer.getBoundingClientRect();
            const origin = originElement.getBoundingClientRect();
            if (!anchor.width)
                return;
            const viewport = window.visualViewport;
            let top = viewport?.offsetTop ?? 0;
            let bottom = top + (viewport?.height ?? window.innerHeight);
            let left = viewport?.offsetLeft ?? 0;
            let right = left + (viewport?.width ?? window.innerWidth);
            if (boundary) {
                const box = boundary.getBoundingClientRect();
                top = Math.max(top, box.top);
                bottom = Math.min(bottom, box.bottom);
                left = Math.max(left, box.left);
                right = Math.min(right, box.right);
            }
            for (let parent = composer.parentElement; parent; parent = parent.parentElement) {
                const overflow = getComputedStyle(parent);
                const box = parent.getBoundingClientRect();
                if (/(auto|scroll|hidden|clip)/u.test(overflow.overflowY)) {
                    top = Math.max(top, box.top);
                    bottom = Math.min(bottom, box.bottom);
                }
                if (/(auto|scroll|hidden|clip)/u.test(overflow.overflowX)) {
                    left = Math.max(left, box.left);
                    right = Math.min(right, box.right);
                }
            }
            const gap = 8;
            const above = Math.max(0, anchor.top - top - gap * 2);
            // Very short viewports use the visible conversation area so controls remain reachable.
            const compact = above < 120;
            const width = Math.max(0, Math.min(maxWidth, anchor.width, right - left - gap * 2));
            const style = {
                width,
                boxSizing: 'border-box',
                left: Math.max(left + gap, Math.min(anchor.left, right - gap - width)) - origin.left,
                maxHeight: Math.max(0, Math.min(maxHeight, compact ? bottom - top - gap * 2 : above)),
                ...(compact ? { top: top + gap - origin.top, bottom: 'auto' } : { top: 'auto', bottom: origin.bottom - anchor.top + gap }),
            };
            setPlacement(current => JSON.stringify(current) === JSON.stringify({ style, compact }) ? current : { style, compact });
        };
        update();
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
        observer?.observe(composer);
        if (boundary)
            observer?.observe(boundary);
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        window.visualViewport?.addEventListener('resize', update);
        window.visualViewport?.addEventListener('scroll', update);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            window.visualViewport?.removeEventListener('resize', update);
            window.visualViewport?.removeEventListener('scroll', update);
        };
    }, [active, maxHeight, maxWidth, popup]);
    return placement;
}
//# sourceMappingURL=useComposerPopupPlacement.js.map